import { ReadonlyVec3 } from 'gl-matrix';
import {
    clearOverlayCanvas,
    keepOnOverlayCanvas,
    OverlayPainter,
    syncCanvasSizeToLayout,
    tracePolyline,
} from '~/engine/core/render/overlayCanvas';
import { displayedCanvasPx } from '~/engine/view/planeViewGeometry';
import type { CanvasFrameGeometry } from '~/engine/view/TextureView';
import { PlaneView } from '~/engine/view/views/PlaneView';
import { sameElements } from '~/utils/array';

/** One uncommitted loop, already in PATIENT (world) mm. `color` is
 *  [0,1] floats. */
export interface GhostPreviewPolygon {
    points: ReadonlyArray<ReadonlyVec3>;
    color: ReadonlyVec3;
}

export type GhostPreviewState = {
    polygons: ReadonlyArray<GhostPreviewPolygon>;
    /** The frame to draw over; the view's live camera without one. */
    frame?: CanvasFrameGeometry;
};

interface PaintedGhost {
    coords: number[];
    width: number;
    height: number;
}

/**
 * Renders dashed "ghost" polygons — an uncommitted preview of geometry
 * nothing has written yet — onto the 2D overlay canvas of the provided
 * {@link PlaneView}. The ONE dashed-preview implementation: contour
 * auto-suggestion adapts its voxel-space marching-squares output onto
 * this (see `ContourAutoSuggestionPreviewRenderer`), and the couch-model
 * preview feeds it world-space loops directly — a ghost-style fix lands
 * in both at once instead of drifting apart across copies.
 *
 * While it holds a state its drawing is kept on the shared overlay canvas
 * ({@link keepOnOverlayCanvas}); without one it leaves the canvas alone.
 *
 * The renderer owns no business logic; it is driven entirely by
 * {@link setState} / {@link clear} calls from the caller. The 2D overlay
 * sits above the WebGL viewport, so re-rendering on every view-state
 * change (slice scroll, pan, zoom) is the caller's responsibility.
 */
export class DashedGhostPreviewRenderer {
    private static readonly DASH_PATTERN = [6, 4];
    private static readonly LINE_WIDTH = 1.5;

    private readonly planeView: PlaneView;
    private readonly canvasContext: CanvasRenderingContext2D;
    private current: GhostPreviewState | undefined;
    private painted: PaintedGhost | undefined;
    private readonly keptPainter: OverlayPainter = () => this.paint();
    private release: (() => void) | undefined;

    public constructor(planeView: PlaneView) {
        this.planeView = planeView;
        const canvas = this.planeView.get2DCanvas;
        if (!canvas) {
            throw new Error(
                'DashedGhostPreviewRenderer: 2D canvas unavailable'
            );
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            throw new Error(
                'DashedGhostPreviewRenderer: 2D context unavailable'
            );
        }
        this.canvasContext = ctx;
    }

    public setState(state: GhostPreviewState): void {
        this.current = state;
        this.release ??= keepOnOverlayCanvas(
            this.canvasContext.canvas,
            this.keptPainter
        );
        this.repaint();
    }

    /** Re-render with the current state (e.g. after a pan/zoom event). */
    public refresh(): void {
        if (!this.current) {
            return;
        }
        const { canvas } = this.canvasContext;
        const painted = this.painted;
        if (
            !painted ||
            canvas.width !== painted.width ||
            canvas.height !== painted.height ||
            canvas.width !== canvas.offsetWidth ||
            canvas.height !== canvas.offsetHeight ||
            !sameElements(this.project(this.current), painted.coords)
        ) {
            this.repaint();
        }
    }

    public clear(): void {
        if (!this.current) {
            return;
        }
        this.current = undefined;
        this.painted = undefined;
        this.release?.();
        this.release = undefined;
        clearOverlayCanvas(this.canvasContext);
    }

    public dispose(): void {
        this.clear();
    }

    private repaint(): void {
        const ctx = this.canvasContext;
        syncCanvasSizeToLayout(ctx);
        clearOverlayCanvas(ctx, this.keptPainter);
        this.paint();
    }

    /** Projects through the state's frame, or the view's live camera without one. */
    private projectionOf(
        state: GhostPreviewState
    ): Pick<PlaneView, 'canvasCoordsFromPatientCoords'> {
        return {
            canvasCoordsFromPatientCoords: (pointMm) =>
                displayedCanvasPx(pointMm, this.planeView, state.frame),
        };
    }

    private project(state: GhostPreviewState): number[] {
        const projection = this.projectionOf(state);
        const coords: number[] = [];
        for (const polygon of state.polygons) {
            for (const point of polygon.points) {
                const [x, y] = projection.canvasCoordsFromPatientCoords(point);
                coords.push(x, y);
            }
        }
        return coords;
    }

    private paint(): void {
        const state = this.current;
        if (!state) {
            return;
        }
        const ctx = this.canvasContext;
        this.painted = undefined;
        ctx.save();
        try {
            ctx.lineWidth = DashedGhostPreviewRenderer.LINE_WIDTH;
            ctx.setLineDash(DashedGhostPreviewRenderer.DASH_PATTERN);

            const projection = this.projectionOf(state);
            for (const polygon of state.polygons) {
                if (polygon.points.length < 2) {
                    continue;
                }
                const R = Math.round(polygon.color[0] * 255);
                const G = Math.round(polygon.color[1] * 255);
                const B = Math.round(polygon.color[2] * 255);
                ctx.fillStyle = `rgba(${R}, ${G}, ${B}, 0.3)`;
                ctx.strokeStyle = `rgba(${R}, ${G}, ${B}, 0.8)`;

                ctx.beginPath();
                tracePolyline(ctx, projection, polygon.points);
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
            }
        } finally {
            ctx.restore();
        }
        this.painted = {
            coords: this.project(state),
            width: ctx.canvas.width,
            height: ctx.canvas.height,
        };
    }
}
